// src/app/services/contact.service.ts

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import {BE_API_AUTH_URL} from '../constants/app.constants';

@Injectable({
  providedIn: 'root',
})
export class ContactService {

  constructor(private http: HttpClient, private authService: AuthService) {}

  // Get all employees by company, excluding those with the role 'Caretaker'
  getEmployees(): Observable<any[]> {
    return this.http
      .get<any[]>(`${BE_API_AUTH_URL}/employees`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Get all employees with the role 'Caretaker' by company
  getCaretakers(): Observable<any[]> {
    return this.http
      .get<any[]>(`${BE_API_AUTH_URL}/caretakers`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Get all services
  getServices(): Observable<any[]> {
    return this.http
      .get<any[]>(`${BE_API_AUTH_URL}/services`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getAssignedServiceTypes(serviceId: string, serviceTypes: string[]): Observable<{ assignedServiceTypes: string[] }> {
    return this.http
      .get<{ assignedServiceTypes: string[] }>(`${BE_API_AUTH_URL}/services/${serviceId}/service-type-usage`, {
        headers: this.authService.createAuthorizationHeader(),
        params: {
          types: serviceTypes.join(','),
        }
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Update an employee
  updateEmployee(employeeId: string, employeeData: any): Observable<any> {
    return this.http
      .put(`${BE_API_AUTH_URL}/employees/${employeeId}`, employeeData, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Update a service
  updateService(serviceId: string, serviceData: any): Observable<any> {
    return this.http
      .put(`${BE_API_AUTH_URL}/services/${serviceId}`, serviceData, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Add a new employee
  addEmployee(employeeData: any): Observable<any> {
    return this.http
      .post(`${BE_API_AUTH_URL}/employees`, employeeData, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Add a new service
  addService(serviceData: any): Observable<any> {
    return this.http
      .post(`${BE_API_AUTH_URL}/services`, serviceData, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deleteService(serviceId: string): Observable<any> {
    return this.http
      .delete(`${BE_API_AUTH_URL}/services/${serviceId}`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deleteEmployee(employeeId: string): Observable<any> {
    return this.http
      .delete(`${BE_API_AUTH_URL}/employees/${employeeId}`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deleteCaretaker(caretakerId: string): Observable<any> {
    // caretakers are employees with role "Caretaker" in the BE, but we keep a separate method for clarity
    return this.http
      .delete(`${BE_API_AUTH_URL}/caretakers/${caretakerId}`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }
}
